import { getLoadTestsConfig } from '@m43/core';
import { useApiClient } from '~/api-client';
import { MapViewRequest, MapViewsResource } from '@mark43/cad-api';
import { useCallback } from 'react';
import { generateEvents, generateStations, generateUnits } from '../load-tests/data';
import type { EsriMapBootstrapData } from '../stores/esri-map-store';
import type { GeoPoint } from '../../location/types';
import { dummyCenter } from '../configuration';

export const useEsriMapResource = () => {
    const apiClient = useApiClient();

    const getMapViewsById = useCallback(
        async (data?: MapViewRequest) => {
            const response = await apiClient.request<MapViewsResource.GetMapCompositeViewForIds>({
                method: 'POST',
                url: 'map_views/composite_view/ids',
                data: data ?? {
                    dispatchAreaIds: [],
                    eventIds: [],
                    unitIds: [],
                    hospitalIds: [],
                },
            });

            return response?.data;
        },
        [apiClient]
    );

    const getMapCompositeView = useCallback(async () => {
        const response = await apiClient.request<MapViewsResource.GetMapCompositeViewForDepartment>(
            {
                method: 'POST',
                url: 'map_views/composite_view',
                data: {
                    dispatchAreaIds: [],
                    eventIds: [],
                    unitIds: [],
                    hospitalIds: [],
                },
            }
        );
        const compositeViewData = response?.data?.data;
        if (!compositeViewData) {
            throw Error('Failed to load bootstrap data for Map.');
        }

        const {
            mapViewportCompositeView: {
                units: mapViewportUnits,
                events: mapViewportEvents,
                hospitals: mapViewportHospitals,
            },
            mapLedgerCompositeView: {
                units: mapLedgerUnits,
                events: mapLedgerEvents,
                hospitals: mapLedgerHospitals,
            },
            stations,
            departmentCenter,
            cfsPriorityLabelsById,
            virtualPatrolLabel,
            priorityOptions,
            dispatchAreaOptions,
            unitStatusOptions,
            agencyTypeOptions,
        } = compositeViewData;

        const center: GeoPoint = {
            latitude: departmentCenter?.latitude ?? dummyCenter.latitude,
            longitude: departmentCenter?.longitude ?? dummyCenter.longitude,
        };

        const esriMapBootstrapData: EsriMapBootstrapData = {
            mapViewportUnits,
            mapViewportEvents,
            mapViewportHospitals,
            mapCenter: center,
            mapLedgerUnits,
            mapLedgerEvents,
            mapLedgerHospitals,
            stations,
            cfsPriorityLabelsById,
            virtualPatrolLabel,
            priorityOptions,
            dispatchAreaOptions,
            unitStatusOptions,
            agencyTypeOptions,
        };

        const loadTestsMapConfig = getLoadTestsConfig('map');
        if (loadTestsMapConfig) {
            const eventsNumber = loadTestsMapConfig?.events ?? 0;
            const unitsNumber = loadTestsMapConfig?.units ?? 0;
            const stationsNumber = loadTestsMapConfig?.stations ?? 0;
            const center = loadTestsMapConfig?.center;

            const mockedStations = generateStations(stationsNumber, center);
            const {
                mapViewportUnits: mockedMapViewportUnits,
                mapLedgerUnits: mockedMapLedgerUnits,
            } = generateUnits(unitsNumber, center);
            const {
                mapViewportEvents: mockedMapViewportEvents,
                mapLedgerEvents: mockedMapLedgerEvents,
            } = generateEvents(eventsNumber, center);

            esriMapBootstrapData.mapViewportUnits = [
                ...mapViewportUnits,
                ...mockedMapViewportUnits,
            ];
            esriMapBootstrapData.mapViewportEvents = [
                ...mapViewportEvents,
                ...mockedMapViewportEvents,
            ];
            esriMapBootstrapData.mapViewportHospitals = [];
            esriMapBootstrapData.mapLedgerUnits = [...mapLedgerUnits, ...mockedMapLedgerUnits];
            esriMapBootstrapData.mapLedgerEvents = [...mapLedgerEvents, ...mockedMapLedgerEvents];
            esriMapBootstrapData.mapLedgerHospitals = [];
            esriMapBootstrapData.stations = [...esriMapBootstrapData.stations, ...mockedStations];
        }

        return esriMapBootstrapData;
    }, [apiClient]);

    return { getMapCompositeView, getMapViewsById };
};
